import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  Routes
} from '@angular/router';
import {
  NgbDropdown,
  NgbDropdownMenu,
  NgbDropdownToggle
} from '@ng-bootstrap/ng-bootstrap';
import { version as appVersion } from '@package';
import { filter, switchMap, tap } from 'rxjs/operators';
import { pmtRoutes } from 'src/app/app.routes';
import { canAccessRoute } from 'src/app/guards/can-access-route';
import { UserDto } from 'src/app/models/user/user-dto';
import { FeedbackEntryComponent } from 'src/app/modules/feedback/feedback-entry/feedback-entry.component';
import { AuthService } from 'src/app/services/api/auth-api';
import { UserMicroservice } from 'src/app/services/api/user-api';
import { EnvironmentService } from 'src/app/services/environment.service';
import { RouteLoaderHandlerService } from 'src/app/services/local/navigation/route-loader-handler.service';
import { NotificationsBellComponent } from '../../modules/notifications/components/notifications-bell/notifications-bell.component';
import { WorkItemsQaGenericEntryComponent } from '../../modules/work-items-micro/work-items-qa-generic-entry/work-items-qa-generic-entry.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    RouterLink,
    RouterLinkActive,
    WorkItemsQaGenericEntryComponent,
    NotificationsBellComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    FeedbackEntryComponent
  ]
})
export class HeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly routeLoaderHandlerService = inject(
    RouteLoaderHandlerService
  );
  private readonly userService = inject(UserMicroservice);
  private readonly environmentService = inject(EnvironmentService);
  private readonly destroyRef = inject(DestroyRef);

  protected routes: Routes = [];
  protected childrenRoutes: Routes = [];
  protected user: UserDto;
  protected readonly version: string = appVersion;
  protected readonly isTestEnv: boolean = this.environmentService.isTest;
  protected readonly isProduction: boolean =
    this.environmentService.isProduction;
  protected readonly isStage: boolean = this.environmentService.isStage;

  ngOnInit(): void {
    // TODO: consider moving next line to AppComponent
    this.routeLoaderHandlerService.startListening();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        switchMap((event: NavigationEnd) =>
          this.userService.applicationUser$.pipe(
            tap(user => {
              this.user = user;
              this.routes = this.getNavRoutes(user);

              this.calculateRoute(event);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Top-level routes the current user passes the guards of (see canAccessRoute)
  private getNavRoutes(user: UserDto | null): Routes {
    return pmtRoutes.filter(
      route =>
        route.data?.title &&
        canAccessRoute(user, route) &&
        !(this.isProduction && route.data.hideProduction)
    );
  }

  calculateRoute(event: NavigationEnd): void {
    this.childrenRoutes = [];

    // No submenu for 404 page
    if (this.router.routerState.root.firstChild?.routeConfig?.path === '**') {
      return;
    }

    let url = event.url;
    if (url === '' || url === '/') {
      url = event.urlAfterRedirects;
    }

    const paths = url.split('/').filter(p => p.trim() !== '');
    let parentPath = '';
    if (paths.length > 0) {
      parentPath = paths[0];
    }

    for (const route of this.routes) {
      if (
        route.path === parentPath &&
        route.children &&
        route.children.length > 0
      ) {
        this.childrenRoutes = route.children.filter(
          c =>
            c.path.trim() !== '' &&
            !(this.isProduction && c.data?.['hideProduction']) &&
            !(this.isStage && c.data?.['hideStage'])
        );
      }
    }
  }

  help(): void {
    window.open(
      'https://app.happeo.com/pages/1hc9htfo3o7ppajkir/Pmt/1hdlnv0ffqnjqjdna6',
      '_blank'
    );
  }

  logout(): void {
    this.auth.logout();
  }
}
